import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import { setUserPermissions, setPortalSettingInfo } from "../reducers/userPermissions";
import type { UserPermissionsData, GetPortalSettingsObjectResponse } from "@ServerTypes/portal/portal.schema";
import type {
  GetUserPermissionsRequest,
  GetUserPermissionsResponse,
} from "@ServerTypes/permissions/permissions.schema";
import type {
  GetNotificationPreferenceRequest,
  GetNotificationPreferenceResponse,
  SaveUserSettingsRequest,
  SaveUserSettingsResponse,
  GetUserSettingsRequest,
  GetUserSettingsResponse,
  DeleteUserSettingsRequest,
  DeleteUserSettingsResponse,
} from "@ServerTypes/userSettings/userSettings.schema";

export function getUserPermissions<T extends Exact<GetUserPermissionsRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // SCHEMA GAP: `getUserPermissionsResponse` declares `data` as `z.unknown()`, while its own
    // description states the payload — an object keyed by requested resourceType, each value
    // `{ permissions: string[] }`, i.e. `UserPermissionsData`. Named at the boundary instead of
    // asserted below. `data` stays optional: the envelope carries it only on success.
    // Fire-and-forget: this thunk is declared to return void, so every `dispatch(getUserPermissions(…))`
    // call site gets void back and has nothing to await; the permissions arrive through the store.
    void axiosWrapper
      .post<GetUserPermissionsResponse & { data?: UserPermissionsData }>(
        `${getBackendUrl()}/permissions/getUserPermissions`,
        data,
      )
      .then((response) => {
        // Only dispatch when the body actually carried the permissions object — the slice's
        // declared state is non-nullable, and its initial `{}` reads as "no permission granted".
        if (response.data.status && response.data.data) {
          dispatch(setUserPermissions(response.data.data));
        }
      });
  };
}
export const getNotificationPreference = async <T extends Exact<GetNotificationPreferenceRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetNotificationPreferenceResponse>(
    `${getBackendUrl()}/userSettings/getNotificationPreference`,
    data,
  );
  return response;
};

export function getPortalSetting() {
  return async (dispatch: AppDispatch) => {
    const responseData = await axiosWrapper.get<GetPortalSettingsObjectResponse>(
      `${getBackendUrl()}/portal/getPortalSettingsObject`,
    );
    // Only dispatch when the body actually carried the settings object: `data` is `.optional()`
    // even on the success arm, while the slice's declared state is non-nullable
    // `PortalSettingInfoData`, which is what makes every consumer's `portalSettingInfo?.` guard
    // load-bearing at runtime. Keeping the initial `{}` is the correct fallback: every permission
    // flag then reads as undefined, i.e. "not granted".
    if (responseData.data.status && responseData.data.data) {
      dispatch(setPortalSettingInfo(responseData.data.data));
    }
  };
}
export const saveUserSettings = async <T extends Exact<SaveUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.post<SaveUserSettingsResponse>(
    `${getBackendUrl()}/userSettings/saveUserSettings`,
    data,
  );
  return response;
};
export const getUserSettings = async <T extends Exact<GetUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.post<GetUserSettingsResponse>(`${getBackendUrl()}/userSettings/getUserSettings`, data);
  return response;
};
export const deleteUserSettings = async <T extends Exact<DeleteUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.post<DeleteUserSettingsResponse>(
    `${getBackendUrl()}/userSettings/deleteUserSettings`,
    data,
  );
  return response;
};
